import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Code2, Palette, PlayCircle, MapPin, Briefcase, Users, Handshake } from 'lucide-react';
import { developers, logoDesigners, clients } from '../data/mock';
import { ParticlesBg } from '../components/layout/ParticlesBg';
import { Section, SectionHeading, PageHero, Ridgeline } from '../components/layout/Section';
import { Reveal, ChapterDivider } from '../components/layout/Tech';
import { SplitProfile } from '../components/layout/SplitProfile';
import { ChartCard, BarsH } from '../components/layout/Charts';
import { Orbit } from '../components/layout/Motion3D';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const freelancers = [
  ...developers.map((m) => ({ ...m, kind: m.role.toLowerCase().includes('developer') ? 'Engineering' : 'Business Development', icon: m.role.toLowerCase().includes('developer') ? Code2 : Briefcase })),
  ...logoDesigners.map((d) => ({ ...d, kind: 'Brand Identity', icon: Palette }))
];

const byCountry = Object.entries(clients.reduce((m, c) => { const k = c.location.split(', ').pop(); return { ...m, [k]: (m[k] || 0) + 1 }; }, {})).map(([name, value]) => ({ name, value })).sort((x, y) => y.value - x.value);
const byRelationship = Object.entries(clients.reduce((m, c) => ({ ...m, [c.relationship]: (m[c.relationship] || 0) + 1 }), {})).map(([name, value]) => ({ name, value }));

export default function People() {
  const [activeDemo, setActiveDemo] = useState(null);

  return (
    <main>
      <PageHero
        tone="indigo"
        eyebrow="Our People"
        title="Freelancers, Partners & Clients"
        subtitle="Built from the mountains of Himachal Pradesh — meet the engineers, partners, and leaders driving our AI vision."
        glows={[
          { color: 'rgba(124,58,237,0.32)', top: '-10%', left: '-6%', size: 560 },
          { color: 'rgba(34,211,238,0.2)', bottom: '-20%', right: '-6%', size: 560 }
        ]}
        ridge
        decor={<Orbit size={720} />}
      >
        <div className="flex flex-wrap justify-center gap-3 mt-10">
          <a href="#freelancers" className="btn-secondary text-xs" style={{ textDecoration: 'none' }}><Users className="w-3.5 h-3.5" /> Freelance team</a>
          <a href="#clients" className="btn-secondary text-xs" style={{ textDecoration: 'none' }}><Handshake className="w-3.5 h-3.5" /> Clients</a>
          <Link to="/#directors" className="btn-secondary text-xs" style={{ textDecoration: 'none' }}>Directors on the home page <ArrowUpRight className="w-3.5 h-3.5" /></Link>
        </div>
      </PageHero>

      {/* ===================== Chapter 01 — Freelance team ===================== */}
      <Section id="freelancers" tone="sky" pattern="dots" padding="py-16" contentWidth={1400} className="scroll-mt-16">
        <ChapterDivider number="01" title="Freelance Team" subtitle="Engineering, business development and brand identity partners who work with Globistaan on a freelance basis." accent="#1D4ED8" />
        <SectionHeading
          title="Engineering & Business Development"
          subtitle="Talented engineers building the next generation of AI-powered products, and business development partners growing the pipeline."
          className="mt-10"
          maxWidth={560}
        />
      </Section>
      {freelancers.map((member, i) => (
        <Section key={member.name} tone={['sky', 'mint', 'sand'][i % 3]} pattern="none" padding="py-0" contentWidth={1600} rule={false} containerClassName="!px-0 md:!px-0">
          <SplitProfile
            index={`Freelance · ${member.kind}`}
            image={member.avatar}
            name={member.name}
            role={member.role}
            bio={member.bio}
            caption={member.kind}
            captionIcon={member.icon}
            reverse={i % 2 === 1}
            accent={['sky', 'green', 'amber'][i % 3]}
            brand={member.portfolio ? { src: '/icons/globistaan-live-logo.png', alt: 'Globistaan logo', bg: '#070A12' } : undefined}
            actions={member.portfolio ? (
              <a href={member.portfolio} target="_blank" rel="noreferrer" className="btn-primary text-xs" style={{ textDecoration: 'none' }}>
                Visit Portfolio <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            ) : undefined}
          />
        </Section>
      ))}

      {/* ===================== Chapter 02 — Clients ===================== */}
      <Section id="clients" tone="ink" pattern="grid" topo aurora padding="py-16" contentWidth={1400} className="scroll-mt-16">
        <ChapterDivider number="02" title="Our Clients" subtitle="From startups to enterprises — clients who trust us with their technology." accent="#22D3EE" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <Reveal className="h-full"><ChartCard title="Clients by country" caption="Where our clients are based." data={byCountry} valueLabel="Clients"><BarsH data={byCountry} /></ChartCard></Reveal>
          <Reveal delay={0.06} className="h-full"><ChartCard title="Clients by relationship" caption="Product owners, product partners and SaaS clients." data={byRelationship} valueLabel="Clients"><BarsH data={byRelationship} categorical /></ChartCard></Reveal>
        </div>
      </Section>
      {clients.map((client, i) => (
        <Section key={client.name} tone={['slate', 'teal', 'navy', 'indigo', 'plum'][i % 5]} pattern="none" aurora={i % 2 === 0} padding="py-0" contentWidth={1600} rule={false} containerClassName="!px-0 md:!px-0">
          <SplitProfile
            index={`Client · ${client.relationship}`}
            image={client.image}
            imagePosition={client.imagePosition}
            fit={client.imageFit || 'cover'}
            name={client.name}
            role={client.industry}
            bio={client.testimonial}
            caption={client.location ? `${client.contact} · ${client.location}` : client.contact}
            captionIcon={MapPin}
            brand={client.logo ? { src: client.logo, alt: `${client.name} logo`, bg: client.logo.includes('dailogue') ? '#070A12' : '#fff' } : undefined}
            meta={client.highlights || []}
            reverse={i % 2 === 1}
            accent={['cyan', 'green', 'sky', 'violet', 'rose'][i % 5]}
            actions={(
              <>
                {client.demoVideo && (
                  <button type="button" onClick={() => setActiveDemo(client)} className="btn-primary btn-accent website-cta">
                    View Product Video <PlayCircle className="w-3.5 h-3.5" />
                  </button>
                )}
                {client.website && (
                  <a href={client.website} target="_blank" rel="noreferrer" className="btn-secondary website-cta">
                    Visit Website <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                )}
              </>
            )}
          />
        </Section>
      ))}

      <Dialog open={Boolean(activeDemo)} onOpenChange={(open) => !open && setActiveDemo(null)}>
        <DialogContent className="project-demo-dialog">
          <DialogHeader>
            <DialogTitle>{activeDemo?.name}</DialogTitle>
            <DialogDescription>Product walkthrough from Globistaan.</DialogDescription>
          </DialogHeader>
          {activeDemo?.demoVideo && (
            <div className="project-demo-frame">
              <iframe
                src={activeDemo.demoVideo}
                title={`${activeDemo.name} product demo`}
                className="absolute inset-0 w-full h-full border-0"
                allow="autoplay; fullscreen; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Section
        tone="navy"
        rule={false}
        aurora
        glows={[
          { color: 'rgba(34,211,238,0.26)', top: '-30%', left: '20%', size: 640 },
          { color: 'rgba(167,139,250,0.22)', bottom: '-30%', right: '10%', size: 560 }
        ]}
        contentWidth={700}
        padding="py-28"
      >
        <ParticlesBg />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center relative z-10"
        >
          <h2 className="h-section mb-5" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)' }}>Join Our Team or Become a Client</h2>
          <p className="lede mx-auto mb-10">We're always looking for talented engineers and ambitious clients who want to build with AI.</p>
          <Link to="/contact" className="btn-primary btn-accent" style={{ padding: '1rem 2.5rem', fontSize: '0.85rem' }}>
            Get in Touch <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <Ridgeline opacity={0.7} height={140} />
      </Section>
    </main>
  );
}
